import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { catchError, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import {
    UserHistoryEvent,
    UserHistoryFilterParams,
    UserHistoryPaginationInfo,
    UserHistoryResponse
} from '../models/user-history.model';

@Injectable({
    providedIn: 'root'
})
export class UserHistoryService {

    private readonly apiUrl = `${environment.apiUrl}/users/history`;

    private eventsSubject = new BehaviorSubject<UserHistoryEvent[]>([]);
    private loadingSubject = new BehaviorSubject<boolean>(false);
    private paginationSubject = new BehaviorSubject<UserHistoryPaginationInfo | null>(null);
    private errorSubject = new BehaviorSubject<string | null>(null);

    public events$ = this.eventsSubject.asObservable();
    public loading$ = this.loadingSubject.asObservable();
    public pagination$ = this.paginationSubject.asObservable();
    public error$ = this.errorSubject.asObservable();

    constructor(private http: HttpClient) { }

    /**
     * Récupère l'historique paginé des actions admin sur les utilisateurs.
     */
    getUserHistory(filters: UserHistoryFilterParams = {}): Observable<UserHistoryResponse> {
        this.loadingSubject.next(true);
        this.errorSubject.next(null);

        let params = new HttpParams();

        if (filters.operationType && filters.operationType !== '') {
            params = params.set('operationType', filters.operationType);
        }
        if (filters.startDate) {
            params = params.set('startDate', filters.startDate);
        }
        if (filters.endDate) {
            params = params.set('endDate', filters.endDate);
        }
        if (filters.adminId) {
            params = params.set('adminId', filters.adminId);
        }
        // Ajout des paramètres de pagination
        if (filters.first !== undefined) {
            params = params.set('first', filters.first.toString());
        }
        if (filters.max !== undefined) {
            params = params.set('max', filters.max.toString());
        }

        // console.log('==> UserHistory Request:', this.apiUrl + '?' + params.toString());

        return this.http.get<UserHistoryResponse>(this.apiUrl, { params })
            .pipe(
                tap(response => {
                    this.eventsSubject.next(response.content ?? []);
                    this.paginationSubject.next({
                        first: response.first,
                        max: response.max,
                        currentPage: response.currentPage,
                        totalPages: response.totalPages,
                        totalElements: response.totalElements,
                        hasNext: response.hasNext,
                        hasPrevious: response.hasPrevious
                    });
                    this.loadingSubject.next(false);
                }),
                catchError(this.handleError.bind(this))
            );
    }

    resetState(): void {
        this.eventsSubject.next([]);
        this.loadingSubject.next(false);
        this.paginationSubject.next(null);
        this.errorSubject.next(null);
    }

    private handleError(error: HttpErrorResponse): Observable<never> {
        this.loadingSubject.next(false);
        let msg: string;
        if (error.error instanceof ErrorEvent) {
            msg = `Erreur client: ${error.error.message}`;
        } else {
            switch (error.status) {
                // case 400: msg = 'Requête invalide. Vérifiez les paramètres.'; break;
                case 400: msg = 'Problème de chargement des données..'; break;
                case 401: msg = 'Non autorisé. Veuillez vous reconnecter.'; break;
                case 403: msg = 'Accès interdit. Permissions insuffisantes.'; break;
                case 404: msg = 'Ressource non trouvée.'; break;
                case 500: msg = 'Erreur serveur interne.'; break;
                case 0: msg = 'Impossible de contacter le serveur.'; break;
                default: msg = `Erreur ${error.status}: ${error.message}`;
            }
        }
        this.errorSubject.next(msg);
        console.error('UserHistory API error:', error);
        return throwError(() => new Error(msg));
    }
}